// Capacity drill — REBUILT 2026-06-08 around Avi's framing:
//   "X פסיכיאטרים × Y שעות = Z סך הכל. נוצלו W. פירוט פר פסיכיאטר."
//
// All numbers are mock until we discover whether Xano models opened slots
// separately from booked meetings. If it doesn't, we cannot compute true
// "hours given" — only "hours booked" — and utilization will need a proxy.

export type TherapistTypeFilter = 'all' | 'psy' | 'cm';

export const CAPACITY_TARGETS = {
  hoursPerPsy: 40,   // Avi 2026-06-08 — monthly target per psychiatrist
  hoursPerCm:  60,   // CM typically delivers more (shorter sessions)
  utilTarget:  0.95, // Blueprint
};

export interface UpcomingMeeting {
  date: string;            // '25/06/2026'
  time: string;            // '10:00'
  type: string;            // Hebrew meeting-type label
}

export interface EmptySlot {
  date: string;
  time: string;
  duration: number;
}

export interface MeetingMix {
  intakeAdult: number;
  intakeYouth: number;
  fuAdult: number;
  fuYouth: number;
}

export interface SegCounts { done: number; noshow: number; canceled: number; }

export interface TherapistRow {
  id: string;
  name: string;
  type: 'psy' | 'cm';
  // Period numbers (default = current month):
  hoursGiven: number;         // total opened slots in their calendar
  hoursBooked: number;        // booked = assigned to a meeting (= done + active + noShow)
  // Breakdown of hoursBooked — populated from the real backend (exec_staff.py).
  // Optional because the legacy mock generators don't set them.
  doneHours?: number;         // meetings that actually happened
  activeHours?: number;       // scheduled, not yet happened (future in month)
  noShowHours?: number;       // booked but patient didn't show (wasted)
  // Raw past/future split (is_past) — free hours derived at aggregate, not here.
  givenPastHours?: number;
  givenFutureHours?: number;
  activePastHours?: number;
  activeFutureHours?: number;
  // Elapsed delivery (is_past=TRUE) — drives the "up-to-now" utilization (= main board).
  donePastHours?: number;
  noShowPastHours?: number;
  intake?: SegCounts;         // this-month delivery: intake (done / no-show / canceled)
  followup?: SegCounts;       // this-month delivery: follow-up
  hoursTarget: number;        // 40 for psy, 60 for cm
  // TRULY bookable free hours (Shachar's available field) — excludes opened-but-
  // BLOCKED slots, unlike hoursGiven−hoursBooked. Optional: mocks don't set it.
  noOpenHours?: boolean;
  availableHours?: number;
  nextFreeSlot?: string | null;  // earliest bookable slot (ISO datetime) or null
  // Inferred Maccabi certification: false = does intakes but 0 Maccabi in last 6mo, so their
  // free hours can only serve Clalit (a small slice of the queue). Optional: mocks don't set it.
  acceptsMaccabi?: boolean;
  caseload?: number;          // active patients held (upcoming OR seen last 90d)
  sustainableCaseload?: number; // psychiatrists: max patients the hours support (0 = n/a)
  loadPct?: number | null;      // caseload / sustainable, % (>100 = over capacity); null = n/a
  futureMeetingCount: number; // from today forward, in period
  futureMix: MeetingMix;
  emptySlotsCount: number;    // open slots in calendar, from today forward
  // Meetings already scheduled to this therapist from now on, all months, any type.
  // Measured straight off the meetings table — it does NOT depend on the nightly
  // calendar probe, which is why it still answers when "פנוי לזימון" reads 0.
  forwardBooked?: number;
  // Measured against the calendar itself, current month, today → month end.
  // undefined = NOT MEASURED (past month, or the measurement has not run yet).
  // That is not the same as 0 and must not render the same: "no time left" and
  // "we did not look" are different answers.
  blockedHours?: number | null;   // blocks landing ON declared hours
  leftHours?: number | null;      // declared − (meetings ∪ blocks) ∩ declared
  emptyPastHours?: number | null; // declared, not blocked, nobody booked — and gone
  // No therapist_load row and no meeting this month — nothing was measured, yet
  // patients are assigned. The row exists so the silence is visible.
  noLoadRow?: boolean;
  upcomingMeetings: UpcomingMeeting[];
  emptySlots: EmptySlot[];
}


// ---------- Aggregates / heroes ----------

export interface CapacitySummary {
  headcountPsy: number;
  headcountCm: number;
  totalGivenPsy: number;
  totalGivenCm: number;
  totalBookedPsy: number;
  totalBookedCm: number;
  totalAvailablePsy: number;   // truly bookable free (excludes blocked)
  totalAvailableCm: number;
  totalOfferedPsy: number;     // booked + available = real offered capacity
  totalOfferedCm: number;
  utilPsy: number;        // 0-1 = booked / offered
  utilCm: number;
  avgPerPsy: number;
  avgPerCm: number;
}

export function getCapacitySummary(rows: TherapistRow[]): CapacitySummary {
  const psy = rows.filter((r) => r.type === 'psy');
  const cm  = rows.filter((r) => r.type === 'cm');
  // "Active" = OPENED a slot this month (given hours > 0), matching the main board's
  // active_actual definition — early in the month most have open calendars but haven't
  // done a meeting yet, so opened-slot is the right count. (Ohad 2026-07-02 — was
  // doneHours>0, which diverged from the board 17→9 / 30→11 on the 2nd.)
  const activePsy = psy.filter((r) => (r.hoursGiven ?? 0) > 0);
  const activeCm  = cm.filter((r) => (r.hoursGiven ?? 0) > 0);

  const sumGivenPsy  = psy.reduce((a, r) => a + r.hoursGiven, 0);
  const sumGivenCm   = cm.reduce((a, r) => a + r.hoursGiven, 0);
  // מנוצל = סלוטים שמולאו בפגישה אמיתית: done (עבר) + active (עתיד מתוכנן) + no-show
  // (מזווית כוח-אדם: המטפל הוקצה/הגיע — שהמטופל לא בא אינו אשמתו). ביטולים נספגים דרך
  // הנפתח (סלוט שבוטל-ונשאר-ריק יורד מהניצולת; אויש-מחדש נספר ע"י הפגישה החדשה).
  // ניצולת = מנוצל / נפתח. (Ohad 2026-06-30)
  const sumBookedPsy = psy.reduce((a, r) => a + r.hoursBooked, 0);
  const sumBookedCm  = cm.reduce((a, r) => a + r.hoursBooked, 0);
  // Available = truly bookable free (Shachar's field, excludes blocked). It's null for
  // current/future months → fall back to opened−booked so offered/util/free don't
  // collapse to booked (the "546/546 = 100%" bug). (Ohad 2026-06-25.)
  // Utilization = booked/given (= the main board). FREE = the REAL bookable field (availableHours),
  // NOT given−booked: `given` includes blocked/non-bookable time, so given−booked overstates "free"
  // (e.g. 3 'leftover' hours but 0 real bookable slots). No real openings → free is 0. (Ohad 2026-06-28)
  const sumAvailPsy  = psy.reduce((a, r) => a + (r.availableHours ?? 0), 0);
  const sumAvailCm   = cm.reduce((a, r) => a + (r.availableHours ?? 0), 0);
  const offeredPsy = sumGivenPsy;
  const offeredCm  = sumGivenCm;
  // ניצולת = מדד "עד היום" (זהה ללוח הראשי): מתוך השעות שנפתחו וכבר חלפו (givenPast),
  // כמה נוצלו = בוצע + נושאו (donePast+noShowPast). לא כל היומן החודשי — אחרת בתחילת
  // חודש הניצולת תמיד נמוכה (מנהלי תיק פותחים ~1386h ליולי כולו). (Ohad 2026-07-02)
  const sumGivenPastPsy = psy.reduce((a, r) => a + (r.givenPastHours ?? 0), 0);
  const sumGivenPastCm  = cm.reduce((a, r) => a + (r.givenPastHours ?? 0), 0);
  const sumUsedPastPsy  = psy.reduce((a, r) => a + (r.donePastHours ?? 0) + (r.noShowPastHours ?? 0), 0);
  const sumUsedPastCm   = cm.reduce((a, r) => a + (r.donePastHours ?? 0) + (r.noShowPastHours ?? 0), 0);

  return {
    headcountPsy: activePsy.length,
    headcountCm:  activeCm.length,
    totalGivenPsy: sumGivenPsy,
    totalGivenCm:  sumGivenCm,
    totalBookedPsy: sumBookedPsy,
    totalBookedCm:  sumBookedCm,
    totalAvailablePsy: sumAvailPsy,
    totalAvailableCm:  sumAvailCm,
    totalOfferedPsy: offeredPsy,
    totalOfferedCm:  offeredCm,
    utilPsy: sumGivenPastPsy > 0 ? sumUsedPastPsy / sumGivenPastPsy : 0,
    utilCm:  sumGivenPastCm  > 0 ? sumUsedPastCm  / sumGivenPastCm  : 0,
    avgPerPsy: activePsy.length > 0 ? sumGivenPsy / activePsy.length : 0,
    avgPerCm:  activeCm.length  > 0 ? sumGivenCm  / activeCm.length  : 0,
  };
}

